import { PurchaseOrder } from "domain/entities/purchase-order";
import { PurchaseOrderInputFilter } from "domain/models/purchase-order-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import { usePurchaseOrdersByFilter } from "presentation/hooks/use-query-purchase-order";
import { useFilterPurchaseOrderStore } from "presentation/stores/use-filter-purchase-order-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatusPurchaseOrderDropdown from "./StatusPurchaseOrderDropdown";
import HeaderPurchaseOrderGrid from "./HeaderPurchaseOrderGrid";
import TypePurchaseOrderDropdown from "./TypePurchaseOrderDropdown";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { Tag } from "primereact/tag";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import {
  usePurchaseOrderUpdateStatusMutation,
  useDeletePurchaseOrderMutation,
} from "presentation/hooks/use-query-purchase-order";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

const PurchaseOrderGrid: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilterPurchaseOrderStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "id",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [justification, setJustification] = useState("");
  const [justificationError, setJustificationError] = useState("");

  const updateStatusMutation = usePurchaseOrderUpdateStatusMutation();
  const deleteMutation = useDeletePurchaseOrderMutation();

  const handleStatusChange = () => {
    if (!justification || justification.trim() === "") {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "A justificativa é obrigatória.",
        life: 3000,
      });
      return;
    }

    if (!selectedPO || !selectedPO.id) return;
    const newStatus = selectedPO.status === "Ativo" ? "Inativo" : "Ativo";

    updateStatusMutation.mutate(
      {
        id: selectedPO.id,
        status: newStatus,
        justification: justification,
      },
      {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: `Purchase Order ${newStatus === "Inativo" ? "inativada" : "ativada"} com sucesso`,
            life: 3000,
          });
          setStatusDialogVisible(false);
          setSelectedPO(null);
          setJustification("");
        },
      },
    );
  };

  const {
    data: timesheets,
    isLoading,
    isError,
    error,
    refetch,
  } = usePurchaseOrdersByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as PurchaseOrderInputFilter);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      page: 1,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== "",
          ),
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const showActionOptions = (rowData: PurchaseOrder) => {
    const isBlocked = rowData.status === "Inativo";
    
    const isDeslocacaoOrPrevencao =
      rowData.type === "Deslocação" ||
      rowData.type === "Prevenção" ||
      rowData.type === "DESLOCACAO" ||
      rowData.type === "PREVENCAO";
    const isSemLimite = rowData.hasLimit === false || isDeslocacaoOrPrevencao;
    
    const canDelete = isSemLimite
      ? (rowData.startAt && new Date() < new Date(rowData.startAt))
      : rowData.plafondLeft === rowData.plafond;

    return (
      <div className="flex gap-5 justify-content-start">
        <i
          className={
            isBlocked
              ? "pi pi-play cursor-pointer text-green-500"
              : "pi pi-pause cursor-pointer text-orange-500"
          }
          title={
            isBlocked ? "Ativar Purchase Order" : "Inativar Purchase Order"
          }
          onClick={() => {
            setSelectedPO(rowData);
            setJustification("");
            setJustificationError("");
            setStatusDialogVisible(true);
          }}
        />
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => navigate(`/purchaseOrders/edit/${rowData.id}`)}
        />
        {canDelete && (
          <i
            className="pi pi-trash cursor-pointer"
            onClick={() => {
              confirmDialog({
                message:
                  "Tem a certeza que deseja excluir esta purchase order?",
                header: "Confirmação",
                icon: "pi pi-info-circle",
                acceptClassName: "p-button-danger",
                acceptLabel: "Sim",
                rejectLabel: "Não",
                accept: () => {
                  deleteMutation.mutate(rowData, {
                    onSuccess: () => {
                      toast.current?.show({
                        severity: "success",
                        summary: "Sucesso",
                        detail: "Purchase order excluída com sucesso",
                        life: 3000,
                      });
                    },
                  });
                },
              });
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={timesheets?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={timesheets?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderPurchaseOrderGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["year", "month", "status"]}
      >
        <Column
          field="name"
          header="Nome"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="poNumber"
          header="Número"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="type"
          header="Tipo"
          sortable
          filter
          filterField="type"
          body={(rowData) => rowData.type}
          showFilterMatchModes={false}
          filterElement={(options) => (
            <TypePurchaseOrderDropdown options={options} />
          )}
        />
        <Column
          field="endAt"
          header="Término"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => formatDateToPT(rowData.endAt)}
        />
        <Column
          field="plafondLeft"
          header="Saldo"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => {
            const isDeslocacaoOrPrevencao =
              rowData.type === "Deslocação" ||
              rowData.type === "Prevenção" ||
              rowData.type === "DESLOCACAO" ||
              rowData.type === "PREVENCAO";

            if (isDeslocacaoOrPrevencao) {
              return (
                <span
                  style={{
                    fontStyle: "italic",
                    color: "var(--text-color-secondary)",
                  }}
                >
                  Sem Limite
                </span>
              );
            }

            if (rowData.hasLimit === false) {
              return (
                <Tag
                  value="Provisória (Sem Limite)"
                  severity="info"
                  rounded
                  style={{
                    fontSize: "0.75rem",
                    backgroundColor: "#e0f2fe",
                    color: "#0369a1",
                    border: "1px solid #bae6fd",
                  }}
                />
              );
            }

            if (rowData.plafondUnit === "HOURS") {
              return `${Number(rowData.plafondLeft).toFixed(1)} h`;
            }

            return new Intl.NumberFormat("pt-PT", {
              style: "currency",
              currency: "EUR",
            }).format(rowData.plafondLeft);
          }}
        />
        <Column
          field="status"
          header="Status"
          sortable
          filter
          filterField="status"
          body={(rowData) => {
            const status = rowData.status;
            let severity: "success" | "warning" | "danger" | "info" = "info";
            let customStyle: React.CSSProperties = {
              fontWeight: "600",
              fontSize: "0.85rem",
              padding: "0.25rem 0.6rem"
            };

            if (status === "Ativo" || status === "ACTIVE" || status === "active") {
              severity = "success";
              customStyle = { ...customStyle, backgroundColor: "#eafaf1", color: "#27ae60", border: "1px solid #c2f0d1" };
            } else if (status === "Inativo" || status === "INACTIVE" || status === "inactive") {
              severity = "warning";
              customStyle = { ...customStyle, backgroundColor: "#fff3e6", color: "#c26000", border: "1px solid #ffe0b2" };
            } else if (status === "Excluído" || status === "DELETED" || status === "deleted") {
              severity = "danger";
              customStyle = { ...customStyle, backgroundColor: "#fdf2f2", color: "#c81e1e", border: "1px solid #fde8e8" };
            }

            return <Tag value={status} severity={severity} rounded style={customStyle} />;
          }}
          showFilterMatchModes={false}
          filterElement={(options) => (
            <StatusPurchaseOrderDropdown options={options} />
          )}
        />

        <Column
          body={showActionOptions}
          header="Ações"
          align="left"
          alignHeader="left"
        />
      </DataTable>

      <Dialog
        header={
          selectedPO?.status === "Ativo"
            ? "Inativar Purchase Order"
            : "Ativar Purchase Order"
        }
        visible={statusDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => {
          setStatusDialogVisible(false);
          setJustification("");
          setJustificationError("");
        }}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => {
                setStatusDialogVisible(false);
                setJustification("");
                setJustificationError("");
              }}
              className="p-button-text"
            />
            <Button
              label="Guardar"
              icon="pi pi-check"
              onClick={handleStatusChange}
              autoFocus
              loading={updateStatusMutation.isPending}
            />
          </div>
        }
      >
        <div className="default-grid">
          <div className="default-4-cols">
            <label htmlFor="justification">
              Justificativa para a alteração de status:
            </label>
            <InputTextarea
              id="justification"
              value={justification}
              onChange={(e) => {
                setJustification(e.target.value);
                if (e.target.value.trim() !== "") setJustificationError("");
              }}
              rows={5}
              className="w-full"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog />
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default PurchaseOrderGrid;
